import {
  LogoutOutlined,
  SettingOutlined,
  UserOutlined,
  CrownOutlined,
  SafetyOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { history, useModel } from '@umijs/max';
import type { MenuProps } from 'antd';
import { Spin, Avatar, Space, Tag } from 'antd';
import { createStyles } from 'antd-style';
import React, { useState, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { outLogin } from '@/services/ant-design-pro/api';
import { getCurrentUserRole } from '@/services/user-management';
import HeaderDropdown from '../HeaderDropdown';

export type GlobalHeaderRightProps = {
  menu?: boolean;
  children?: React.ReactNode;
};

export const AvatarName = () => {
  const { initialState } = useModel('@@initialState');
  const { currentUser } = initialState || {};
  return <span className="anticon">{currentUser?.name}</span>;
};

const useStyles = createStyles(({ token }) => {
  return {
    action: {
      display: 'flex',
      height: '48px',
      marginLeft: 'auto',
      overflow: 'hidden',
      alignItems: 'center',
      padding: '0 12px',
      cursor: 'pointer',
      borderRadius: token.borderRadius,
      transition: 'all 0.3s',
      '&:hover': {
        backgroundColor: token.colorBgTextHover,
      },
    },
    userInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    userName: {
      fontWeight: 500,
      fontSize: '14px',
      color: token.colorText,
    },
    roleTag: {
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: 500,
    },
  };
});

export const AvatarDropdown: React.FC<GlobalHeaderRightProps> = ({
  menu,
  children,
}) => {
  const [userRole, setUserRole] = useState<string | null>(null);
  const [username, setUsername] = useState<string>('');

  useEffect(() => {
    const fetchUserRole = async () => {
      const result = await getCurrentUserRole();
      if (result.success && result.data) {
        setUserRole(result.data);
      }
      const storedUsername = localStorage.getItem('cow_current_username');
      if (storedUsername) {
        setUsername(storedUsername);
      }
    };
    fetchUserRole();
  }, []);

  const getRoleDisplay = (role: string | null) => {
    switch (role) {
      case 'super_admin':
        return { label: 'Super Admin', icon: <CrownOutlined />, color: 'gold' };
      case 'admin':
        return { label: 'Admin', icon: <SafetyOutlined />, color: 'blue' };
      case 'viewer':
        return { label: 'Viewer', icon: <EyeOutlined />, color: 'default' };
      default:
        return { label: 'User', icon: <UserOutlined />, color: 'default' };
    }
  };

  /**
   * Đăng xuất - xóa token và redirect về login
   */
  const loginOut = async () => {
    try {
      // Xóa tất cả tokens và thông tin user TRƯỚC KHI call API
      sessionStorage.removeItem('cow_session_token');
      localStorage.removeItem('cow_remember_token');
      localStorage.removeItem('cow_current_username');
      localStorage.removeItem('cow_current_user_id');
      localStorage.removeItem('cow_last_login');
      
      // Call logout API (không cần đợi response)
      outLogin().catch(() => {
        // Ignore error - user đã logout ở client rồi
        console.log('Logout API error - ignored');
      });
      
      // Redirect về trang login ngay lập tức
      history.replace('/user/login');
    } catch (error) {
      console.error('Logout error:', error);
      // Vẫn redirect về login dù có lỗi
      history.replace('/user/login');
    }
  };
  const { initialState, setInitialState } = useModel('@@initialState');

  const onMenuClick: MenuProps['onClick'] = (event) => {
    const { key } = event;
    if (key === 'logout') {
      flushSync(() => {
        setInitialState((s) => ({ ...s, currentUser: undefined }));
      });
      loginOut();
      return;
    }
    history.push(`/account/${key}`);
  };

  const { styles } = useStyles();
  const roleDisplay = getRoleDisplay(userRole);

  const loading = (
    <span className={styles.action}>
      <Spin
        size="small"
        style={{
          marginLeft: 8,
          marginRight: 8,
        }}
      />
    </span>
  );

  if (!initialState) {
    return loading;
  }

  const { currentUser } = initialState;

  if (!currentUser || !currentUser.name) {
    return loading;
  }

  const menuItems: MenuProps['items'] = [
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Đăng xuất',
      danger: true,
    },
  ];

  return (
    <HeaderDropdown
      menu={{
        selectedKeys: [],
        onClick: onMenuClick,
        items: menuItems,
      }}
    >
      <span className={styles.action}>
        <Space className={styles.userInfo}>
          <Avatar
            size="default"
            icon={<UserOutlined />}
            src={currentUser.avatar}
            style={{
              backgroundColor: roleDisplay.color === 'gold' ? '#faad14' : roleDisplay.color === 'blue' ? '#1890ff' : '#d9d9d9',
            }}
          />
          <Space direction="vertical" size={0} style={{ lineHeight: 1.2 }}>
            <span className={styles.userName}>
              {username || currentUser.name}
            </span>
            <Tag
              icon={roleDisplay.icon}
              color={roleDisplay.color}
              className={styles.roleTag}
              style={{ margin: 0 }}
            >
              {roleDisplay.label}
            </Tag>
          </Space>
        </Space>
      </span>
    </HeaderDropdown>
  );
};
